import * as React from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClientProvider } from '@tanstack/react-query'
import { ClerkProvider } from '@clerk/clerk-react'
import { queryClient } from './lib/queryClient'
import {
    RouterProvider,
    createBrowserRouter,
    createRoutesFromChildren,
    matchRoutes,
    useLocation,
    useNavigationType
} from 'react-router-dom'
import { loadStripe } from '@stripe/stripe-js'
import { Elements } from '@stripe/react-stripe-js'
import './_settings/base.css'

// Posthog
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react'
import { Posthog } from '@capacitor/posthog'

// Capacitor 
import { CapacitorStripeProvider } from '@capacitor-community/stripe/react';
import { LiveUpdate } from '@capawesome/capacitor-live-update'
import { NativeBridge } from '@capacitor/native-bridge'
import { OneSignal } from '@capacitor/one-signal'
import { Preferences } from '@capacitor/preferences'

// Sentry
import * as Sentry from '@sentry/react'

// ROUTES
import RootRoute from './routes/index.tsx'

// TYPES
import type { StripeElementsOptions } from '@stripe/stripe-js'

// Utils
import { platform } from '@utils/capacitor-helper'


const stripe = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY)
if (!stripe) throw new Error("Failed to load Stripe");
/** https://docs.stripe.com/js/elements_object/create */
const options: StripeElementsOptions = {
    fonts: [{ cssSrc: 'https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&family=Open+Sans:ital,wght@0,300..800;1,300..800&display=swap' }],
    currency: 'usd',
    mode: 'setup'
}

const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY
if (!clerkPubKey) throw new Error('Missing Clerk Publishable Key')


async function init() {
    // Initialize Sentry before doing anything else
    if (!import.meta.env.DEV)
        Sentry.init({
            dsn: import.meta.env.VITE_SENTRY_DSN,
            environment: import.meta.env.MODE,
            sendDefaultPii: true,
            enableLogs: true,
            integrations: [
                Sentry.reactRouterV6BrowserTracingIntegration({
                    useEffect: React.useEffect,
                    useLocation,
                    useNavigationType,
                    createRoutesFromChildren,
                    matchRoutes
                }),
                Sentry.consoleLoggingIntegration({
                    levels: ['log', 'warn', 'error']
                })
            ],
            tracesSampleRate: 1.0
        })

    const router = Sentry.wrapCreateBrowserRouterV6(createBrowserRouter)([RootRoute])
    console.log('[ LiveUpdate ] [ launch ] platform()', platform)
    const signInUrl = platform === 'web'
        ? new URL('/sign-in', window.location.origin).toString()
        : '/sign-in'

    if (platform === 'web') {
        if (!import.meta.env.DEV) {
            posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_KEY, {
                api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
                defaults: '2025-05-24',
                enable_recording_console_log: true,
                person_profiles: 'identified_only'
            });
        }

        createRoot(document.getElementById('root')!).render(
            <Elements stripe={stripe} options={options}>
                <PostHogProvider client={posthog}>
                    <QueryClientProvider client={queryClient}>
                        <ClerkProvider
                            publishableKey={clerkPubKey}
                            clerkJSVariant="headless"
                            signInUrl={signInUrl}
                            signUpUrl={signInUrl}
                            signInFallbackRedirectUrl="/"
                            signUpFallbackRedirectUrl="/"
                        >
                            <RouterProvider router={router} />
                        </ClerkProvider>
                    </QueryClientProvider>
                </PostHogProvider>
            </Elements>
        )
    } else {
        try {
            let shouldReload = true

            const performLiveUpdate = async () => {
                // Check beta preference to determine channel
                const { value: betaValue } = await Preferences.get({ key: 'beta' })
                const isBeta = betaValue === 'true'

                await LiveUpdate.setChannel({
                    channel: import.meta.env.DEV ? 'development' : isBeta ? 'staging' : 'production'
                })
                console.log('[ LiveUpdate ] [ launch ] setChannel()', { channel: import.meta.env.DEV ? 'development' : isBeta ? 'staging' : 'production', isBeta })

                const { currentBundleId, previousBundleId } = await LiveUpdate.ready()
                console.log('[ LiveUpdate ] [ launch ] ready()', { currentBundleId, previousBundleId })

                if (currentBundleId === null && previousBundleId === null) {
                    const { bundleId, downloadUrl } = await LiveUpdate.fetchLatestBundle()
                    console.log('[ LiveUpdate ] [ launch ] fetchLatestBundle()', bundleId)

                    if (bundleId && downloadUrl) {
                        console.log("[ LiveUpdate ] [ launch ] download, set reload")
                        try {
                            await LiveUpdate.downloadBundle({ bundleId, url: downloadUrl })
                        } catch (downloadError: any) {
                            if (downloadError?.message !== 'bundle already exists.') {
                                throw downloadError
                            }
                            console.log('[ LiveUpdate ] [ launch ] bundle already downloaded, proceeding to set')
                        }
                        await LiveUpdate.setNextBundle({ bundleId })

                        if (shouldReload) await LiveUpdate.reload()
                        else console.log('[ LiveUpdate ] [ launch ] skipping reload - timed out')
                    }
                }

                if (currentBundleId !== null) {
                    const { nextBundleId } = await LiveUpdate.sync()
                    console.log('[ LiveUpdate ] [ launch ] sync()', nextBundleId)

                    if (nextBundleId !== null && nextBundleId !== currentBundleId) {
                        if (shouldReload) await LiveUpdate.reload()
                        else console.log('[ LiveUpdate ] [ launch ] skipping reload - timed out')
                    }
                }
            }

            // Timeout promise that rejects after 5 seconds
            const timeout = new Promise<void>((_, reject) => {
                setTimeout(() => {
                    shouldReload = false
                    reject(new Error('[ LiveUpdate ] [ launch ] timed out'))
                }, 5000)
            })

            // Race the update against the timeout
            await Promise.race([performLiveUpdate(), timeout])
        } catch (error) {
            console.error('[ LiveUpdate ] [ launch ] issue performing live update', error)
        }

        try {
            OneSignal.initialize({
                appId: import.meta.env.VITE_ONE_SIGNAL_APP_ID
            })

            if (!import.meta.env.DEV) {
                await Posthog.setup({
                    apiKey: import.meta.env.VITE_PUBLIC_POSTHOG_KEY,
                    host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
                    enableSessionReplay: true,
                    sessionReplayConfig: {
                        screenshotMode: true,
                        maskAllTextInputs: false,
                        maskAllImages: false,
                        maskAllSandboxedViews: false,
                        captureLogs: true
                    }
                })

                await Posthog.startSessionRecording()
            }

            // Enable swipe gestures for the WebView
            await NativeBridge.enableSwipeGestures()

            // Enable native iOS pull-to-refresh
            await NativeBridge.enablePullToRefresh()
        } catch (error) {
            console.error('[ Init ] issue initializing external services', error)
        }

        createRoot(document.getElementById('root')!).render(
            <CapacitorStripeProvider publishableKey={import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY}>
                <Elements stripe={stripe} options={options}>
                    <QueryClientProvider client={queryClient}>
                        <ClerkProvider
                            publishableKey={clerkPubKey}
                            clerkJSVariant="headless"
                            signInUrl={signInUrl}
                            signUpUrl={signInUrl}
                            signInFallbackRedirectUrl="/"
                            signUpFallbackRedirectUrl="/"
                        >
                            <RouterProvider router={router} />
                        </ClerkProvider>
                    </QueryClientProvider>
                </Elements>
            </CapacitorStripeProvider>
        )
    }
}

init()
